import { useEffect, useState } from "react";
import { useAutoOpen } from "@/hooks/useAutoOpen";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO, toDateInput } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Truck } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";
import { StokKayuBar, rit } from "@/components/app/bakar/StokKayuBar";
import AkunSelect from "@/components/app/AkunSelect";

const empty = { tanggal: todayISO(), supplier: "", jumlah_rit: 1, harga_total: 0, biaya_bongkar: 0, keterangan: "" };

export default function KayuMasuk() {
  const [items, setItems] = useState([]);
  const [stok, setStok] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/kayu-masuk"), api.get("/stok-kayu")]);
      setItems(a.data); setStok(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });
  const openNew = () => { setEditId(null); setForm({ ...empty, tanggal: todayISO() }); setOpen(true); };
  useAutoOpen(openNew);
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it, tanggal: toDateInput(it.tanggal) || todayISO(), supplier: it.supplier ?? "", keterangan: it.keterangan ?? "" }); setOpen(true); };

  const total = Number(form.harga_total || 0) + Number(form.biaya_bongkar || 0);
  const perRit = Number(form.jumlah_rit) > 0 ? Math.round(total / Number(form.jumlah_rit)) : 0;

  const save = async (e) => {
    e?.preventDefault();
    if (!form.tanggal) { toast.error("Tanggal wajib diisi"); return; }
    if (!(Number(form.jumlah_rit) > 0)) { toast.error("Jumlah rit wajib diisi (lebih dari 0)"); return; }
    const payload = {
      tanggal: form.tanggal, supplier: form.supplier || "", jumlah_rit: Number(form.jumlah_rit),
      harga_total: Number(form.harga_total || 0), biaya_bongkar: Number(form.biaya_bongkar || 0), keterangan: form.keterangan || "", akun_id: form.akun_id || null,
    };
    try {
      if (editId) await api.put(`/kayu-masuk/${editId}`, payload);
      else await api.post("/kayu-masuk", payload);
      toast.success(editId ? "Kayu masuk diperbarui" : "Kayu masuk tersimpan & stok kayu bertambah");
      setOpen(false); await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus data kayu masuk ini? Pengeluaran terkait juga dipindah ke Sampah.")) return;
    try { await api.delete(`/kayu-masuk/${id}`); toast.success("Terhapus"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const { page, setPage, totalPages, paged, total: count, pageSize } = usePagination(items, 10);

  return (<>
    <PageHeader title="Kayu Masuk" subtitle="Tahap 1 — catat setiap truk/rit kayu yang datang. Biaya langsung masuk Pengeluaran & menambah stok kayu."
      actions={<Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-kayu-btn"><Plus size={14} className="mr-1"/>Kayu Masuk</Button>}/>
    <div className="p-4 md:p-8 space-y-4">
      <StokKayuBar stok={stok}/>
      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs md:text-sm" data-testid="kayu-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="p-3">Tanggal</th><th className="p-3">Supplier</th><th className="p-3 text-right">Jumlah</th>
                <th className="p-3 text-right">Harga Kayu</th><th className="p-3 text-right">Ongkos Bongkar</th>
                <th className="p-3 text-right">Total</th><th className="p-3 text-right">Harga / Rit</th>
                <th className="p-3 hide-mobile">Keterangan</th><th className="p-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-slate-400">Belum ada kayu masuk. Klik "Kayu Masuk" saat truk datang.</td></tr>}
              {paged.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`kayu-row-${it.id}`}>
                  <td className="p-3 whitespace-nowrap">{tanggalID(it.tanggal)}</td>
                  <td className="p-3">{it.supplier || "-"}</td>
                  <td className="p-3 text-right font-mono font-semibold">{rit(it.jumlah_rit)}</td>
                  <td className="p-3 text-right font-mono">{rupiah(it.harga_total)}</td>
                  <td className="p-3 text-right font-mono">{rupiah(it.biaya_bongkar)}</td>
                  <td className="p-3 text-right font-mono font-semibold">{rupiah(it.total_biaya)}</td>
                  <td className="p-3 text-right font-mono brand-text">{rupiah(it.harga_per_rit)}</td>
                  <td className="p-3 text-slate-500 hide-mobile">{it.keterangan || "-"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => openEdit(it)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-kayu-${it.id}`}><Pencil size={14}/></button>
                      <button onClick={() => del(it.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-kayu-${it.id}`}><Trash2 size={14} className="text-rose-600"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={count} pageSize={pageSize} testid="kayu-pagination"/>
      </div>
    </div>

    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="kayu-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck size={18} className="brand-text"/>{editId ? "Ubah" : "Catat"} Kayu Masuk</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Tanggal Datang <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")} data-testid="kayu-tanggal"/></div>
          <div className="space-y-1.5"><Label>Jumlah (Rit/Truk) <span className="text-rose-500">*</span></Label><Input type="number" min="0" step="0.01" value={form.jumlah_rit} onChange={upd("jumlah_rit")} data-testid="kayu-rit"/></div>
          <div className="md:col-span-2 space-y-1.5"><Label>Supplier</Label><Input value={form.supplier} onChange={upd("supplier")} placeholder="mis. Pak Budi" data-testid="kayu-supplier"/></div>
          <div className="space-y-1.5"><Label>Harga Kayu (Rp)</Label><CurrencyInput value={form.harga_total} onValueChange={upd("harga_total")} data-testid="kayu-harga"/></div>
          <div className="space-y-1.5"><Label>Ongkos Bongkar/Angkut (Rp)</Label><CurrencyInput value={form.biaya_bongkar} onValueChange={upd("biaya_bongkar")} data-testid="kayu-bongkar"/></div>
          <div className="md:col-span-2 grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Total Biaya</Label><div className="h-9 px-3 flex items-center border border-slate-200 bg-slate-50 font-mono font-semibold" data-testid="kayu-total">{rupiah(total)}</div></div>
            <div className="space-y-1.5"><Label>Harga / Rit</Label><div className="h-9 px-3 flex items-center border border-slate-200 bg-blue-50 font-mono font-semibold brand-text" data-testid="kayu-per-rit">{rupiah(perRit)}</div></div>
          </div>
<AkunSelect className="md:col-span-2" label="Dibayar dari akun" value={form.akun_id} onChange={(v) => setForm((f) => ({ ...f, akun_id: v }))} testid="kayu-akun"/>
          <div className="md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} data-testid="kayu-keterangan"/></div>
          <button type="submit" className="hidden" aria-hidden="true"/>
        </form>
        <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-sm p-2">
          Total biaya otomatis dicatat di Pengeluaran (Bahan Baku Arang). Kayu masuk ke stok dan nanti diambil saat Mulai Bakar.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
          <Button onClick={save} className="brand-btn rounded-sm" data-testid="kayu-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
